import type { PublicListing } from './businessListingApi';

const isProduction = import.meta.env.PROD;

function marketplaceEndpoint(path: string): string {
  const clean = path.replace(/^\/+|\/+$/g, '');

  if (isProduction) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/api/public/marketplace/${clean}`;
  }

  const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;
  if (apiServerUrl) {
    const base = apiServerUrl.replace(/\/+$/, '');
    const withApi = base.includes('/api') ? base : `${base}/api`;
    return `${withApi}/public/marketplace/${clean}`;
  }

  return `http://localhost:5183/api/public/marketplace/${clean}`;
}

export interface MarketplaceListing {
  id: string;
  name: string;
  summary: string;
  industries: string[];
  categorySlugs: string[];
  suburb: string;
  state: string;
  councilAreas: string[];
  logoFileName: string | null;
  backgroundColor: string;
  website: string;
  phone: string;
  latitude: number | null;
  longitude: number | null;
  distanceKm: number | null;
  /**
   * The business's published listing, or null when it has none, still has it in draft, or this
   * record came from the search endpoint.
   *
   * Mirrors `MarketplaceListingDto.Listing`. Only `businesses/{id}` ever fills it; `listings`
   * sends it as an explicit null rather than omitting it, so a business has one shape on the wire
   * whichever endpoint produced it. The type is `PublicListing` from `businessListingApi` and not
   * a second declaration of the same fields — one payload, one definition.
   */
  listing: PublicListing | null;
  /**
   * The hero picture on the business's PUBLISHED listing, or null when it has no listing, still
   * has it in draft, or published one without a hero.
   *
   * Mirrors `MarketplaceListingDto.HeroImageId`, and unlike `listing` it is filled by BOTH
   * endpoints — a card on the browse page draws the hero and nothing else off the listing, so this
   * is the one value that travels there.
   *
   * A non-null id is not a promise the picture will load: it names a file that may be missing from
   * storage, and the id-keyed route answers 404 rather than substituting anything. Anything
   * rendering it needs an error fallback, not a truthiness check.
   */
  heroImageId: string | null;
  /**
   * The credibility rail, or null when this record came from the search endpoint — computing it
   * needs an enquiry aggregate per business, so only `businesses/{id}` fills it.
   *
   * Mirrors `MarketplaceListingDto.Credibility`.
   */
  credibility: MarketplaceCredibility | null;
}

/**
 * Mirrors `MarketplaceCredibilityDto`.
 *
 * **The client applies no thresholds of its own.** The server has ALREADY withheld what should not
 * be shown: `responseRatePercent` is null below the volume threshold, never a small-sample number,
 * and `jobsWon` is null at zero, never `0`. So the only rule this side of the wire is "render what
 * is here, hide what is null" — a second rule in a second place is how the two drift apart.
 *
 * `showroomCity`/`showroomState` are null together when the business has no city on file, and
 * `showroomState` is null alone when only the state is missing, so a reader is never shown a bare
 * comma.
 *
 * `abn` and `abnVerifiedAt` are published so a visitor can check the claim on the ABR themselves;
 * `abnVerified` is exactly `abnVerifiedAt !== null` and carries nothing the date does not.
 */
export interface MarketplaceCredibility {
  /** From the business's creation date. Always present. */
  memberSinceYear: number;
  showroomCity: string | null;
  showroomState: string | null;
  abn: string | null;
  /** ISO date-time, or null when an admin has never verified the ABN. */
  abnVerifiedAt: string | null;
  abnVerified: boolean;
  /** Enquiries marked handled/won/lost over the last 90 days. Null when withheld — never 0-ish. */
  responseRatePercent: number | null;
  /** Enquiries marked won, all time. Null at zero. */
  jobsWon: number | null;
}

export interface MarketplaceCategory {
  slug: string;
  label: string;
  icon: string;
  tint: string;
  color: string;
  count: number;
}

export interface ResolvedLocation {
  query: string;
  councilArea: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface MarketplaceListingsResult {
  total: number;
  resolvedLocation: ResolvedLocation | null;
  /** True when nobody serves the searched council and the nearest are shown instead. */
  fallbackUsed: boolean;
  items: MarketplaceListing[];
  /**
   * Ids of the businesses holding a live PAID placement — at most
   * `FeaturedWeek.MaxPerWeek` (10) of them, and usually none.
   *
   * Mirrors `MarketplaceListingsResult.FeaturedIds`. `items` is already ordered with these first,
   * so this is not needed to sort — it is what lets a caller pull them out and present them
   * differently, and what lets it label them honestly as promoted.
   *
   * Absent on an older server build, so treat it as possibly undefined rather than trusting the
   * type: a missing field must read as "nothing is featured", not throw.
   */
  featuredIds?: string[];
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Marketplace request failed (${response.status})`);
  return (await response.json()) as T;
}

export function getMarketplaceCategories(): Promise<MarketplaceCategory[]> {
  return getJson<MarketplaceCategory[]>(marketplaceEndpoint('categories'));
}

export function getMarketplaceListings(params: {
  what?: string;
  where?: string;
  category?: string;
} = {}): Promise<MarketplaceListingsResult> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value && value.trim()) query.set(key, value.trim());
  }
  const suffix = query.toString() ? `?${query}` : '';
  return getJson<MarketplaceListingsResult>(`${marketplaceEndpoint('listings')}${suffix}`);
}

export function getMarketplaceBusiness(id: string): Promise<MarketplaceListing> {
  return getJson<MarketplaceListing>(marketplaceEndpoint(`businesses/${encodeURIComponent(id)}`));
}
